/** Shared modal chrome for overlays that belong to the game area. */

export interface GameModal {
  backdrop: HTMLDivElement;
  body: HTMLDivElement;
  close: () => void;
}

let modalId = 0;

/** Create a centered, dismissible modal over a game surface.
 *
 * The modal owns the small bits of interaction chrome that every dialog needs:
 * backdrop dismissal, Escape, initial focus, a local tab loop, and focus
 * restoration. Callers only have to render their body and decide what their
 * buttons do. */
export function createGameModal(parent: HTMLElement, title: string, id?: string): GameModal {
  const backdrop = document.createElement("div");
  backdrop.className = "game-modal-backdrop";
  if (id) backdrop.id = id;

  const card = document.createElement("div");
  card.className = "game-modal";
  card.setAttribute("role", "dialog");
  card.setAttribute("aria-modal", "true");
  card.tabIndex = -1;

  const head = document.createElement("div");
  head.className = "game-modal-head";
  const heading = document.createElement("h2");
  heading.className = "game-modal-title";
  heading.textContent = title;
  const headingId = id ? `${id}-title` : `game-modal-title-${++modalId}`;
  heading.id = headingId;
  card.setAttribute("aria-labelledby", headingId);
  const closeButton = document.createElement("button");
  closeButton.type = "button";
  closeButton.className = "game-modal-close";
  closeButton.setAttribute("aria-label", `Close ${title}`);
  closeButton.textContent = "×";
  head.append(heading, closeButton);

  const body = document.createElement("div");
  body.className = "game-modal-body";
  card.append(head, body);
  backdrop.appendChild(card);
  parent.appendChild(backdrop);

  const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  let closed = false;

  const closeModal = (): void => {
    if (closed) return;
    closed = true;
    backdrop.remove();
    if (previousFocus?.isConnected) previousFocus.focus();
  };

  const focusable = (): HTMLElement[] => Array.from(
    card.querySelectorAll<HTMLElement>(
      "button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex=\"-1\"])",
    ),
  );

  const onKeyDown = (ev: KeyboardEvent): void => {
    if (ev.key === "Escape") {
      ev.preventDefault();
      closeModal();
      return;
    }
    if (ev.key !== "Tab") return;
    const elements = focusable();
    if (elements.length === 0) {
      ev.preventDefault();
      card.focus();
      return;
    }
    const first = elements[0]!;
    const last = elements[elements.length - 1]!;
    if (ev.shiftKey && document.activeElement === first) {
      ev.preventDefault();
      last.focus();
    } else if (!ev.shiftKey && document.activeElement === last) {
      ev.preventDefault();
      first.focus();
    }
  };

  // `close` is part of the public handle and is also used by callers while
  // processing a server response, so keep it idempotent and focus-safe.
  closeButton.addEventListener("click", closeModal);
  backdrop.addEventListener("pointerdown", (ev) => {
    if (ev.target === backdrop) {
      ev.preventDefault();
      ev.stopPropagation();
      closeModal();
    }
  });
  backdrop.addEventListener("keydown", onKeyDown);

  // Focus after the dialog is mounted so keyboard users can immediately act
  // on it. The close affordance is always present, even before a caller adds
  // its form controls.
  const first = focusable()[0] ?? closeButton;
  first.focus();

  return { backdrop, body, close: closeModal };
}
